import { axios } from 'Fetcher'
import { getLowercaseSearchParams } from 'Lib/AppUtils'
import { getHashedEmail } from 'Lib/Crypto'
import { Store } from 'redux'

const PIXEL_ID = 'ID-2016853728679608321'
const NEWSBREAK_SCRIPT_ID = 'newsbreak-pixel'
const NEWSBREAK_SCRIPT_SRC = 'https://static.newsbreak.com/business/tracking/nbpixel.js'
const NBAPI_ROUTING_PATH = '/api/collect/nbapi'
const DISABLE_ON_DEV = false

interface PixelEvent {
  nb_value: string
}

type ConversionEvent = PixelEvent & {
  nb_cid?: string | null
}

// NewsBreak Pixel Initialization
if (!window.nbpix) {
  const now = Date.now()
  const nbpixTracker: any = (window.nbpix = (...args: any) => {
    nbpixTracker.process
      ? nbpixTracker.process.apply(nbpixTracker, args)
      : nbpixTracker.queue.push(args)
  })
  nbpixTracker.queue = []
  nbpixTracker.t = now

  window.nbpix('init', PIXEL_ID)
  window.nbpix('event', 'pageload')
}

const getNewsBreakScript = () => document.querySelector(`#${NEWSBREAK_SCRIPT_ID}`)

let store: Store | null = null

export const initNewsBreakService = (storeInstance: Store) => {
  store = storeInstance

  if (__DEV__ && DISABLE_ON_DEV) {
    return
  }

  if (getNewsBreakScript()) {
    return // Already initialized
  }

  const roundedDate = 864e5 * Math.ceil(Date.now() / 864e5) // Round up to nearest day
  const script = document.createElement('script')
  script.async = true
  script.src = `${NEWSBREAK_SCRIPT_SRC}?t=${roundedDate}`
  script.id = NEWSBREAK_SCRIPT_ID

  const firstScript = document.querySelector('script')
  firstScript?.parentNode?.insertBefore(script, firstScript)
}

export const revokeNewsBreakConsent = () => {
  if (__DEV__ && DISABLE_ON_DEV) {
    return
  }

  getNewsBreakScript()?.remove() // Remove script element from DOM
}

type NewsBreakEvent = 'complete_payment' | 'initiate_checkout' | 'complete_registration'

const logConversionsEvent = async (eventType: NewsBreakEvent, params: ConversionEvent) => {
  try {
    if (__DEV__ && DISABLE_ON_DEV) {
      return
    }

    const hashedEmail = await getHashedEmail(store)

    // Error handling is done on the backend side
    await axios.get(NBAPI_ROUTING_PATH, {
      params: {
        event_type: eventType,
        callback: params.nb_cid,
        nb_value: params.nb_value,
        nb_email: hashedEmail
      }
    })
  } catch (error) {
    logError(
      'web_newsbreak_CAPIError',
      new Error(`NewsBreak CAPI event logging failed: ${error?.message}`),
      error?.response?.data ?? error?.message ?? error,
      true
    )
  }
}

const getNewsBreakClickId = (): string | null => {
  return getLowercaseSearchParams().get('nb_cid') ?? getLowercaseSearchParams().get('newsbreak_cid')
}

export const trackNewsBreakEvent = (eventType: NewsBreakEvent, params: PixelEvent) => {
  try {
    if (__DEV__ && DISABLE_ON_DEV) {
      return
    }

    if (!window.nbpix) {
      return
    }

    const updatedParams: ConversionEvent = {
      ...params,
      nb_cid: getNewsBreakClickId()
    }

    window.nbpix('event', eventType, updatedParams)
    void logConversionsEvent(eventType, updatedParams)
  } catch (error) {
    logError(
      'web_newsbreak_PixelError',
      new Error(`NewsBreak Pixel event logging failed: ${error?.message}`),
      error?.response?.data ?? error?.message ?? error,
      true
    )
  }
}
